import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import SplitPane from 'react-split-pane';

import { black } from 'lib/css/colors';
import pointerOnHover from 'lib/css/pointerOnHover';
import { PRODUCT_SCORE_RECALCULATE } from 'lib/networking/endpoints';
import { ProductType } from 'lib/enums/ProductType';
import usePost from 'lib/hooks/usePost';

function ProductScoreCard({ score = 0, p0_score = 0, product = {}, productType }) {
  const [productScore, setProductScore] = useState(score);
  const [p0Score, setP0Score] = useState(p0_score);
  const [jsonLogs, setJsonLogs] = useState();
  const [debugSummaryScoreLog, setDebugSummaryScoreLog] = useState();
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [stuckTooltip, setStuckTooltip] = useState(null);

  const { postData: calculateScore, loading } = usePost(PRODUCT_SCORE_RECALCULATE, res => {
    setDebugSummaryScoreLog(res?.data?.data?.debug_summary_score_data);
    setJsonLogs(res?.data?.data?.debug_data);
    setProductScore(res?.data?.data?.score);
    setP0Score(res?.data?.data?.p0_score);
  });

  // Reset all state when product changes
  useEffect(() => {
    setProductScore(score);
    setP0Score(p0_score);
    setJsonLogs(null);
    setDebugSummaryScoreLog(null);
  }, [product?.id, score, p0_score]);

  // Sections that use a single aggregate score — no per-subsection breakdown.
  const SECTION_LEVEL_SECTIONS = new Set(['Claims', 'Top-Off']);

  // Summarized Version: one row per section showing max score and final score.
  const renderSectionRow = (sectionName, sectionData, summaryScore) => {
    let maxScore = 0;

    Object.keys(sectionData).forEach(subSectionName => {
      maxScore += Number(sectionData[subSectionName]['score_groups'].max_score || 0);
    });

    return (
      <tr key={sectionName}>
        <td>{sectionName}</td>
        <td>{Number(maxScore.toFixed(2))}</td>
        <td>{Number(summaryScore.total_section_score?.toFixed(2))}</td>
      </tr>
    );
  };

  // Detailed Version: single aggregate row for Claims / Top-Off sections.
  const renderSectionLevelDetailRow = (sectionName, sectionData, summaryScore) => {
    let totalExistingAttrs = 0;
    let totalProductAttrs = 0;
    let totalMaxScore = 0;
    const allProductAttributes = [];

    Object.values(sectionData).forEach(sub => {
      totalExistingAttrs += Number(sub['attribute_counts'].existing_attribute_count || 0);
      totalProductAttrs += Number(sub['attribute_counts'].product_existing_attribute_count || 0);
      totalMaxScore += Number(sub['score_groups'].max_score || 0);
      if (Array.isArray(sub['attribute_counts'].product_existing_attributes)) {
        allProductAttributes.push(...sub['attribute_counts'].product_existing_attributes);
      }
    });

    return (
      <tr key={sectionName}>
        <td>{sectionName}</td>
        <td>—</td>
        <td>{totalExistingAttrs}</td>
        <td>
          <OverlayTrigger
            placement="top-start"
            trigger={['hover', 'focus']}
            overlay={
              <Tooltip className={css(styles.leftAlignedTooltip)}>
                {allProductAttributes.length > 0 ? (
                  <ul className={classNames(css(styles.leftAlignedTooltip), 'm-0 pl-3')}>
                    {allProductAttributes.map((attr, i) => (
                      <li key={i}>{attr}</li>
                    ))}
                  </ul>
                ) : (
                  'No attributes found'
                )}
              </Tooltip>
            }
          >
            <span>{totalProductAttrs}</span>
          </OverlayTrigger>
        </td>
        <td>{Number(totalMaxScore.toFixed(2))}</td>
        <td>{Number(summaryScore?.total_section_score?.toFixed(2) || 0)}</td>
      </tr>
    );
  };

  return (
    <>
      <div className="d-flex">
        <p className="mr-2 mt-3">
          Product Score: {Number(productScore?.toFixed(2)).toLocaleString('en-US')}
        </p>
        <OverlayTrigger
          placement="top-start"
          overlay={<Tooltip>Re-calculate product score</Tooltip>}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faSync}
              className={classNames(pointerOnHover, 'ml-2', loading && 'fa-spin')}
              onClick={() =>
                calculateScore({ entity_type: productType, fsa_id: product.id, debug_mode: true })
              }
            />
          </div>
        </OverlayTrigger>
        {!loading && jsonLogs && (
          <div className="ml-3 mt-3">
            <span
              className={css(styles.logsLink)}
              onClick={() => {
                setShowLogsModal(true);
              }}
            >
              View Logs
            </span>
          </div>
        )}

        <Modal
          size="xl"
          show={showLogsModal}
          onHide={() => setShowLogsModal(false)}
          dialogClassName={css(styles.extraLargeModal)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Product Score Logs</Modal.Title>
          </Modal.Header>
          <Modal.Body className={classNames(css(styles.logsModalContent))}>
            <SplitPane
              primary="first"
              split="vertical"
              defaultSize="30%"
              className={classNames(css(styles.splitPane), 'mx-4 mt-4')}
            >
              <div className={classNames(css(styles.tableContainer), 'mr-3')}>
                <h5>Summarized Version</h5>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Max Score</th>
                      <th>Final Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jsonLogs &&
                      Object.keys(jsonLogs).map(sectionName =>
                        renderSectionRow(
                          sectionName,
                          jsonLogs[sectionName],
                          debugSummaryScoreLog[sectionName],
                        ),
                      )}
                  </tbody>
                </Table>
              </div>
              <div className={classNames(css(styles.tableContainer), 'ml-3')}>
                <h5>Detailed Version</h5>
                <Table striped bordered hover>
                  <thead className={css(styles.stickyHeader)}>
                    <tr>
                      <th>Section</th>
                      <th>Sub Section</th>
                      <th>Existing Attr Count</th>
                      <th>Product Existing Attr Count</th>
                      <th>Max Score</th>
                      <th>Product Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jsonLogs &&
                      Object?.keys(jsonLogs)?.map(sectionName => (
                        <React.Fragment key={sectionName}>
                          {SECTION_LEVEL_SECTIONS.has(sectionName)
                            ? renderSectionLevelDetailRow(
                                sectionName,
                                jsonLogs[sectionName],
                                debugSummaryScoreLog?.[sectionName],
                              )
                            : Object?.keys(jsonLogs[sectionName])?.map(subSectionName => (
                                <tr key={sectionName + subSectionName}>
                                  <td onClick={() => setStuckTooltip(null)}>{sectionName}</td>
                                  <td onClick={() => setStuckTooltip(null)}>{subSectionName}</td>
                                  <td onClick={() => setStuckTooltip(null)}>
                                    {
                                      jsonLogs[sectionName][subSectionName]['attribute_counts']
                                        .existing_attribute_count
                                    }
                                  </td>
                                  <td>
                                    <OverlayTrigger
                                      placement="top-start"
                                      trigger={['hover', 'focus']}
                                      show={
                                        stuckTooltip === `${sectionName}-${subSectionName}`
                                          ? true
                                          : undefined
                                      }
                                      overlay={
                                        <Tooltip className={css(styles.leftAlignedTooltip)}>
                                          {Array.isArray(
                                            jsonLogs[sectionName][subSectionName][
                                              'attribute_counts'
                                            ].product_existing_attributes,
                                          ) ? (
                                            <ul
                                              className={classNames(
                                                css(styles.leftAlignedTooltip),
                                                'm-0 pl-3',
                                              )}
                                            >
                                              {jsonLogs[sectionName][subSectionName][
                                                'attribute_counts'
                                              ].product_existing_attributes.map((attr, index) => (
                                                <li key={index}>{attr}</li>
                                              ))}
                                            </ul>
                                          ) : (
                                            'No attributes found'
                                          )}
                                        </Tooltip>
                                      }
                                    >
                                      <span
                                        className={css(styles.tooltipTrigger)}
                                        onClick={() => {
                                          const tooltipId = `${sectionName}-${subSectionName}`;
                                          if (stuckTooltip === tooltipId) {
                                            setStuckTooltip(null);
                                          } else {
                                            setStuckTooltip(tooltipId);
                                          }
                                        }}
                                      >
                                        {
                                          jsonLogs[sectionName][subSectionName]['attribute_counts']
                                            .product_existing_attribute_count
                                        }
                                      </span>
                                    </OverlayTrigger>
                                  </td>
                                  <td onClick={() => setStuckTooltip(null)}>
                                    {Number(
                                      jsonLogs[sectionName][subSectionName][
                                        'score_groups'
                                      ].max_score?.toFixed(2),
                                    )}
                                  </td>
                                  <td onClick={() => setStuckTooltip(null)}>
                                    {Number(
                                      jsonLogs[sectionName][
                                        subSectionName
                                      ].score_for_sub_section?.toFixed(2),
                                    ) || 0}
                                  </td>
                                </tr>
                              ))}
                          <tr>
                            <td colSpan="6"></td>
                          </tr>
                        </React.Fragment>
                      ))}
                  </tbody>
                </Table>
              </div>
              <div />
            </SplitPane>
          </Modal.Body>
        </Modal>
      </div>
      {productType === ProductType.DISTRIBUTOR && (
        <div>
          <p className="mr-2">
            P0 Score: {Number(p0Score?.toFixed(2) || 0).toLocaleString('en-US')}
          </p>
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  logsLink: {
    textDecoration: 'underline',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  logsModalContent: {
    height: '80vh',
    overflow: 'hidden',
  },
  tableContainer: {
    maxHeight: '80vh',
    overflow: 'auto',
    marginBottom: '20px',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    background: 'white',
    zIndex: 1,
    boxShadow: `${black} 0 2px 2px -1px`,
  },
  leftAlignedTooltip: {
    textAlign: 'left',
  },
  splitPane: {
    position: 'relative',
  },
  extraLargeModal: {
    width: '90vw',
    maxWidth: '90vw',
  },
});

ProductScoreCard.propTypes = {
  score: PropTypes.number,
  p0_score: PropTypes.number,
  product: PropTypes.object.isRequired,
  productType: PropTypes.string.isRequired,
};

export default ProductScoreCard;
